import { useCallback, useMemo, useRef } from 'react';
import config from '../aws-config';
import { useAuthContext } from '../context/AuthContext';

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const cache = new Map();
const pendingRequests = new Map();

export function clearApiCache() {
  cache.clear();
  pendingRequests.clear();
}

async function parseResponse(response) {
  const text = await response.text();
  const payload = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const error = new Error(payload?.error?.message || payload?.message || 'API request failed');
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
}

function getCacheKey(method, path) {
  if (method !== 'GET') return null;
  return `${method}:${path}`;
}

function getCachedData(cacheKey) {
  const entry = cache.get(cacheKey);
  if (!entry) return null;

  const now = Date.now();
  if (now - entry.timestamp > CACHE_TTL) {
    cache.delete(cacheKey);
    return null;
  }

  return entry.data;
}

export default function useApi() {
  const { getToken } = useAuthContext();
  const tokenRef = useRef(getToken);

  // Update ref on every render to always get the latest token
  tokenRef.current = getToken;

  const request = useCallback(
    async (method, path, body, options = {}) => {
      const { forceRefresh = false } = options;
      const cacheKey = getCacheKey(method, path);

      // Check cache for GET requests unless forceRefresh is true
      if (cacheKey && !forceRefresh) {
        const cached = getCachedData(cacheKey);
        if (cached) {
          return cached;
        }

        // Return pending request if one is already in flight
        if (pendingRequests.has(cacheKey)) {
          return pendingRequests.get(cacheKey);
        }
      }

      const makeRequest = async () => {
        const token = await tokenRef.current();
        const headers = {
          'Content-Type': 'application/json',
        };

        if (token) {
          headers.Authorization = token;
        }

        const response = await fetch(`${config.apiEndpoint}${path}`, {
          method,
          headers,
          body: body !== undefined ? JSON.stringify(body) : undefined,
        });

        const payload = await parseResponse(response);

        if (method === 'GET') {
          if (cacheKey) {
            cache.set(cacheKey, {
              data: payload,
              timestamp: Date.now(),
            });
            pendingRequests.delete(cacheKey);
          }
        } else {
          // On successful mutations (POST, PUT, DELETE), clear GET cache completely
          clearApiCache();
        }

        return payload;
      };

      if (cacheKey && !forceRefresh) {
        const promise = makeRequest();
        promise.catch(() => pendingRequests.delete(cacheKey));
        pendingRequests.set(cacheKey, promise);
        return promise;
      }

      return makeRequest();
    },
    [],
  );

  const get = useCallback((path, forceRefresh = false) => request('GET', path, undefined, { forceRefresh }), [request]);
  const post = useCallback((path, body) => request('POST', path, body), [request]);
  const put = useCallback((path, body) => request('PUT', path, body), [request]);
  const del = useCallback((path) => request('DELETE', path), [request]);
  const clearCache = useCallback(() => clearApiCache(), []);

  return useMemo(
    () => ({ get, post, put, del, clearCache }),
    [get, post, put, del, clearCache],
  );
}
